import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Sparkles } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function LikeRow({ item }) {
  const { t } = useI18n();
  const p = item.profile || {};
  const img = photoUrl(p.main_photo_url);
  const inner = (
    <div
      className="flex items-center gap-4 p-4 rounded-lg border transition-colors hover:bg-[hsl(var(--muted))]"
      style={{ borderColor: "hsl(var(--nvl-border))" }}
      data-testid={`like-row-${item.target_user_id}`}
    >
      <div
        className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-display text-xl"
        style={{
          backgroundImage: img ? `url(${img})` : undefined,
          backgroundSize: "cover", backgroundPosition: "center",
          backgroundColor: "hsl(var(--muted))",
          color: "hsl(var(--nvl-accent))",
        }}
      >
        {img ? null : (p.display_name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{p.display_name}</p>
        {p.city ? (
          <p className="text-xs flex items-center gap-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            <MapPin size={11} /> {p.city}
          </p>
        ) : null}
      </div>
      {item.is_match ? (
        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest"
          style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
          data-testid={`like-match-badge-${item.target_user_id}`}
        >
          <Sparkles size={10} /> {t("app.likes.isMatch")}
        </span>
      ) : (
        <span className="text-[11px]" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.likes.waiting")}
        </span>
      )}
    </div>
  );
  return item.is_match && item.match_id ? (
    <Link to={`/app/matches/${item.match_id}`} data-testid={`like-open-match-${item.target_user_id}`}>{inner}</Link>
  ) : (
    <Link to={`/app/discover/${item.target_user_id}`}>{inner}</Link>
  );
}

export default function Likes() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/interactions/likes/sent");
        setItems(data.items || []);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-3xl mx-auto" data-testid="page-likes">
      <div>
        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          <Heart size={12} className="inline mr-1 -mt-0.5" /> {t("app.likes.title")}
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-display">{t("app.likes.title")}</h1>
        <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.likes.subtitle")}
        </p>
      </div>

      <div className="mt-8 space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse h-20 rounded-lg" style={{ backgroundColor: "hsl(var(--muted))" }} />
          ))
        ) : error ? (
          <div className="nvl-card p-6" data-testid="likes-error">
            <p className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="nvl-card p-10 text-center" data-testid="likes-empty">
            <Heart size={22} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.likes.empty")}
            </p>
          </div>
        ) : (
          items.map((it) => <LikeRow key={it.target_user_id} item={it} />)
        )}
      </div>
    </div>
  );
}
